import { faTimesCircle } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useContext, useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import Dropzone from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import DialogContext from '../Authentication/DialogContext'
import { checkFormIntegrity, getCustomErrorClass, getDataArray, getFormData, getFormValidation, hideDialog } from '../utils/DialogUtil'
import ErrorMessage, { closeErrorAlert } from '../utils/ErrorMessage'
import { ConfirmDialogProps } from './ConfirmDialog'

const FormFields: FC<FormFieldsProps> = (props) => {
  const { t } = useTranslation()
  const {
    forwardedRef,
    setSubmitting,
    header = t('editDialog.header'),
    data = [],
    initCustom = [''],
    labelGridSize = 2,
    onSubmit,
    onSubmitExtraArgs = [],
    handleResponseCustom,
    canForceUpdate = false,
  } = props

  const [validated, setValidated] = useState(false)
  const [prevState, setPrevState] = useState('')
  const { editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)
  const [error, setError] = React.useState(null as any)
  const [showError, setShowError] = React.useState(false)
  const [custom, setCustom] = useState(initCustom)
  const formData = getFormData(custom, data)
  const [form, setFormValues] = useState(formData)
  const [formValidation, setFormValidation] = useState(getFormValidation(formData))
  checkFormIntegrity(formData, form, setFormValues, setFormValidation)

  const dataArray = getDataArray(custom, data)

  function setCustomErrorClass(element: any) {
    const { id, validity } = element
    const customErrorClass = getCustomErrorClass(validity)
    formValidation[id] = customErrorClass
  }

  const handleSubmit = (event: any) => {
    setSubmitting(true)
    const formTarget = event.currentTarget
    event.preventDefault()
    if (formTarget.checkValidity() === false) {
      event.stopPropagation()
      Array.prototype.forEach.call(formTarget.elements, (element: any) => {
        if ('button' !== element.type) {
          setCustomErrorClass(element)
        }
      })
      setSubmitting(false)
    } else {
      submit(false)
    }
    setValidated(true)
  }

  function onSubmitFunction(force: boolean) {
    return canForceUpdate ? onSubmit(form, force, ...onSubmitExtraArgs) : onSubmit(form, ...onSubmitExtraArgs)
  }

  function submit(force: boolean) {
    onSubmitFunction(force)
      .then((message: any) => {
        if (handleResponseCustom !== undefined) {
          handleResponseCustom(message)
        } else if (typeof message === 'string') {
          const confirmDialogProps: ConfirmDialogProps = {
            show: true,
            header: header,
            body: message,
            submitText: t('ok'),
            showCancelButton: false,
          }
          hideDialog(editDialogPropsSetter)
          confirmDialogPropsSetter(confirmDialogProps)
        } else {
          hideDialog(editDialogPropsSetter)
        }
      })
      .catch((err: any) => {
        if (canForceUpdate) {
          if (!force && err.message.forceable === true && err.config.data === prevState) {
            submit(true)
            return
          }
          setPrevState(err.config.data)
        }
        setError(err.message)
        setShowError(true)
      })
      .finally(() => setSubmitting(false))
  }

  const updateControlField = (e: any, dependentUpdate?: any[]) => {
    const toAdd = {
      ...form,
      [e.target.id]: e.target.value,
      ...dependentUpdate?.map((id) => {
        return { [id]: e.target.value }
      }),
    }
    console.log(toAdd)
    setFormValues(toAdd)
    setValidated(false)
  }

  const updateTypeAheadField = (e: any, id: string) => {
    const value = e.length ? (e[0].value ? e[0].value : e[0]) : ''
    setFormValues((form: any) => {
      return {
        ...form,
        [id]: value,
      }
    })
    setValidated(false)
  }

  const updateCheckField = (e: any) => {
    const id = e.target.id
    const checked = e.target.checked
    setFormValues((form: any) => {
      return {
        ...form,
        [id]: checked,
      }
    })
    setValidated(false)
  }

  const updateFileField = (id: string, file: any) => {
    setFormValues((form: any) => {
      return {
        ...form,
        [id]: file,
      }
    })
    setValidated(false)
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      {dataArray.map((editData) => {
        const { type, render = true } = editData
        let renderBoolean: boolean = true
        if (typeof render === 'boolean') {
          renderBoolean = render
        } else {
          renderBoolean = render(custom)
        }
        if (!renderBoolean) {
          return <React.Fragment key={type}></React.Fragment>
        }

        if (['checkbox', 'radio', 'switch'].includes(type)) {
          const { groupLabel, type, groupName, inline = false, checkboxes } = editData as FormDataCheckbox

          return (
            <Form.Group as={Row} key={groupName}>
              <Form.Label column sm={labelGridSize} className='my-auto text-wrap text-break text-break-all'>
                {groupLabel}
              </Form.Label>
              <Col sm={12 - labelGridSize} className='my-auto'>
                {checkboxes.map((checkbox) => {
                  const { id, label = '', disabled = false, title, onChange } = checkbox
                  return (
                    <Form.Check
                      key={id}
                      type={type}
                      id={id}
                      label={label}
                      name={groupName}
                      checked={form[id]}
                      onChange={(e: any) => {
                        updateCheckField(e)
                        if (onChange) {
                          onChange(e.target.checked, setCustom)
                        }
                      }}
                      inline={inline}
                      disabled={disabled}
                      title={title}
                    />
                  )
                })}
              </Col>
            </Form.Group>
          )
        } else if (type === 'file') {
          const { id, label, required, requiredMessage } = editData as FormDataFile
          const isFileUploaded: boolean = form[id] !== undefined && form[id] instanceof Blob && form[id].size > 0
          return (
            <Form.Group as={Row} key={id}>
              <Form.Label column sm={labelGridSize} className='my-auto text-wrap text-break text-break-all'>
                {label}
              </Form.Label>
              <Col sm={12 - labelGridSize}>
                <div className={`d-flex border  ${validated && formValidation[id] !== '' && formValidation[id] !== 'valid' ? 'border-danger' : 'border-dark'}`}>
                  <Dropzone multiple={false} onDrop={(acceptedFiles) => updateFileField(id, acceptedFiles[0])}>
                    {({ getRootProps, getInputProps, isDragActive, fileRejections }) => {
                      const tooManyFiles = fileRejections.length > 0 && fileRejections[0].errors[0].code === 'too-many-files'

                      return (
                        <div className='my-auto w-100'>
                          <div {...getRootProps()}>
                            <input {...getInputProps()} required={required} id={id} />
                            {isFileUploaded && !isDragActive && <span className='pl-3'>{`<${label} Uploaded> click here or drop a file to reupload!`}</span>}
                            {!isFileUploaded && !isDragActive && (
                              <span className='pl-3'>
                                Click here or drop a file to upload! {tooManyFiles && <span className='text-danger'>Multiple files cannot be uploaded</span>}
                              </span>
                            )}
                            {isDragActive && <span className='pl-3'>Drop file here</span>}
                          </div>
                        </div>
                      )
                    }}
                  </Dropzone>
                  <Button
                    className={isFileUploaded ? 'visible' : 'invisible'}
                    disabled={!isFileUploaded}
                    onClick={() => {
                      updateFileField(id, undefined)
                    }}>
                    <FontAwesomeIcon icon={faTimesCircle} />
                  </Button>
                </div>
                <span
                  className={`feedback-${formValidation[id]} invalid-feedback ${
                    validated && formValidation[id] !== '' && formValidation[id] !== 'valid' ? 'd-block' : 'd-none'
                  }`}>
                  <span className='msg-required'>{requiredMessage}</span>
                  <span className='msg-invalid'>{t('invalidMessage', { label })}</span>
                </span>
              </Col>
            </Form.Group>
          )
        } else {
          const {
            id,
            label,
            options,
            dependentUpdate,
            onChange,
            autoComplete,
            placeholder = '',
            required = false,
            requiredMessage,
            min,
            minInvalidMessage,
            max,
            maxInvalidMessage,
            minLength,
            minLengthInvalidMessage,
            maxLength,
            maxLengthInvalidMessage,
            pattern,
            patternInvalidMessage,
            step,
            stepInvalidMessage,
            size,
            plaintext = false,
            readOnly = false,
            disabled = false,
          } = editData as FormDataControl

          if (type === 'label') {
            return (
              <Form.Group as={Row} key={id}>
                <Form.Label column className='text-wrap text-break'>
                  {label}
                </Form.Label>
              </Form.Group>
            )
          }

          return (
            <Form.Group as={Row} key={id}>
              <Form.Label column sm={labelGridSize} className='text-wrap text-break text-break-all'>
                {label}
              </Form.Label>
              <Col sm={12 - labelGridSize}>
                {type === 'textarea' && (
                  <Form.Control
                    as='textarea'
                    rows={5}
                    id={id}
                    required={required}
                    minLength={getNumber(minLength)}
                    maxLength={getNumber(maxLength)}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    value={form[id]}
                    onChange={(e: any) => {
                      updateControlField(e, dependentUpdate)
                      if (onChange) {
                        onChange(e, setCustom)
                      }
                    }}
                    size={size}
                    plaintext={plaintext}
                    readOnly={readOnly}
                    disabled={disabled}
                  />
                )}
                {type === 'select' && (
                  <Form.Control
                    as='select'
                    id={id}
                    required={required}
                    placeholder={placeholder}
                    value={form[id]}
                    onChange={(e: any) => {
                      updateControlField(e, dependentUpdate)
                      if (onChange) {
                        onChange(e, setCustom)
                      }
                    }}
                    size={size}
                    plaintext={plaintext}
                    readOnly={readOnly}
                    disabled={disabled}>
                    {options &&
                      options.map((option: any) => {
                        if (typeof option === 'string') {
                          return (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          )
                        } else {
                          return (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          )
                        }
                      })}
                  </Form.Control>
                )}
                {type === 'multiSelect' && (
                  <Select
                    id={id}
                    key={id}
                    required={required}
                    placeholder={placeholder}
                    onChange={(selected: any) => {
                      const e: any = getMultiSelectEvent(id, selected)
                      updateControlField(e, dependentUpdate)
                      if (onChange) {
                        onChange(e, setCustom)
                      }
                    }}
                    size={size}
                    plaintext={plaintext}
                    readOnly={readOnly}
                    disabled={disabled}
                    defaultValue={[...form[id]]}
                    options={
                      options &&
                      options.map((option: any) => {
                        if (typeof option === 'string') {
                          return { value: option, label: option }
                        } else {
                          return option
                        }
                      })
                    }
                    closeMenuOnSelect={false}
                    isMulti>
                    {!disabled && (
                      <input key={`hidden-${id}`} tabIndex={-1} autoComplete='off' style={{ opacity: 0, height: 0 }} value={form[id]} required={required} />
                    )}
                  </Select>
                )}
                {type === 'typeAhead' && (
                  <Typeahead
                    id={id}
                    placeholder={placeholder}
                    options={options ? options : []}
                    labelKey={(option) => (option.label ? option.label : option)}
                    onChange={(e: any) => {
                      updateTypeAheadField(e, id)
                      if (onChange) {
                        onChange(e, setCustom)
                      }
                    }}
                    disabled={disabled}
                  />
                )}

                {type !== 'textarea' && type !== 'multiSelect' && type !== 'select' && type !== 'typeAhead' && (
                  <Form.Control
                    as='input'
                    id={id}
                    required={required}
                    min={min}
                    max={max}
                    minLength={getNumber(minLength)}
                    maxLength={getNumber(maxLength)}
                    pattern={pattern}
                    step={step}
                    type={type}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    value={form[id]}
                    onChange={(e: any) => {
                      updateControlField(e, dependentUpdate)
                      if (onChange) {
                        onChange(e, setCustom)
                      }
                    }}
                    size={size}
                    plaintext={plaintext}
                    readOnly={readOnly}
                    disabled={disabled}>
                    {options &&
                      options.map((option: any) => {
                        if (typeof option === 'string') {
                          return <option key={option}>{option}</option>
                        } else {
                          return (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          )
                        }
                      })}
                  </Form.Control>
                )}

                <Form.Control.Feedback type='invalid' id={`invalid-${id}-feedback`}>
                  <span className={`feedback-${formValidation[id]}`}>
                    <span className='msg-required'>{requiredMessage}</span>
                    <span className='msg-min'>{minInvalidMessage}</span>
                    <span className='msg-max'>{maxInvalidMessage}</span>
                    <span className='msg-minLength'>{minLengthInvalidMessage}</span>
                    <span className='msg-maxLength'>{maxLengthInvalidMessage}</span>
                    <span className='msg-pattern'>{patternInvalidMessage}</span>
                    <span className='msg-step'>{stepInvalidMessage}</span>
                    <span className='msg-invalid'>{t('invalidMessage', { label })}</span>
                  </span>
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
          )
        }
      })}
      <ErrorMessage error={error} show={showError} onClose={closeErrorAlert(setError, setShowError)} />
      <Button type='submit' ref={forwardedRef} className='d-none' />
    </Form>
  )
}

function getNumber(value: any) {
  const numberValue: number = Number(value)
  if (isNaN(numberValue)) {
    return undefined
  }
  return numberValue
}

function getMultiSelectEvent(id: any, selected: any) {
  const value = selected ? selected.map((s: any) => s.value) : []
  return { target: { id, value } }
}

export type FormFieldsProps = {
  forwardedRef: React.RefObject<any>
  setSubmitting: React.Dispatch<any>
  header?: string | JSX.Element
  data: (FormDataControl | FormDataCheckbox | FormDataFile)[] | ((custom: string[]) => (FormDataControl | FormDataCheckbox | FormDataFile)[])
  initCustom?: string[]
  labelGridSize?: number

  onSubmit: (...args: any[]) => Promise<string | undefined | void>
  handleResponseCustom?: (message: any) => void
  onSubmitExtraArgs?: any[]
  canForceUpdate?: boolean
}

export type FormDataControl = {
  id: string
  value?: string | any[]
  type: 'number' | 'text' | 'textarea' | 'password' | 'email' | 'hidden' | 'select' | 'typeAhead' | 'multiSelect' | 'label'

  min?: string
  minInvalidMessage?: string
  max?: string
  maxInvalidMessage?: string
  minLength?: string
  minLengthInvalidMessage?: string
  maxLength?: string
  maxLengthInvalidMessage?: string
  pattern?: string
  patternInvalidMessage?: string
  step?: string
  stepInvalidMessage?: string

  options?: any[]
  selected?: any
  dependentUpdate?: any[]
  onChange?: (e: any, setCustom: React.Dispatch<any>) => void

  label?: string
  autoComplete?: string
  placeholder?: string
  size?: 'sm' | 'lg'

  render?: boolean | ((custom: string[]) => boolean)
  required?: boolean
  requiredMessage?: string
  plaintext?: boolean
  readOnly?: boolean
  disabled?: boolean
}

export type FormDataCheckbox = {
  groupLabel: string | JSX.Element
  groupName: string
  type: 'checkbox' | 'radio' | 'switch'
  inline?: boolean
  render?: boolean | ((custom: string[]) => boolean)

  checkboxes: {
    id: string
    value?: boolean
    title?: string
    disabled?: boolean
    label?: React.ReactNode
    onChange?: (e: any, setCustom: React.Dispatch<any>) => void
  }[]
}

export type FormDataFile = {
  id: string
  value?: any
  type: 'file'
  label?: string
  render?: boolean | ((custom: string[]) => boolean)
  required?: boolean
  requiredMessage?: string
}

export default FormFields
